'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { posApi } from '@/api/pos-api'
import { useAuth } from '@/auth/AuthProvider'
import { categories as demoCategories, currentUser as demoUser, orders as demoOrders, products as demoProducts, tables as demoTables, activityLogs as demoActivityLogs } from '@/lib/pos-data'
import type { ActivityLog, Category, Order, OrderItem, Product, Reservation, Table, User, Zone } from '@/lib/pos-types'
import { isApiEnabled } from '@/routes/role-routing'
import { computeOrderTotals } from '@/lib/totals'
import { mergeOfflineQueueMutation, useLocalStorageJsonState } from '@/lib/offline-queue'
import { toMs } from '@/lib/utils'

type QueuedMutation =
  | { id: string; kind: 'updateTable'; tableId: string; payload: unknown; createdAt: string }
  | { id: string; kind: 'updateOrder'; orderId: string; payload: unknown; createdAt: string }
  | { id: string; kind: 'createOrder'; payload: unknown; createdAt: string }

type MeseroContextValue = {
  apiEnabled: boolean
  apiOnline: boolean
  currentUser: User
  tables: Table[]
  orders: Order[]
  products: Product[]
  categories: Category[]
  activityLogs: ActivityLog[]
  reservations: Reservation[]
  zones: string[]
  zoneItems?: Zone[];
  offlineQueueCount: number
  refreshNow: () => Promise<void>
  openTable: (tableId: string, diners?: number) => Promise<boolean>
  unlockTableByCode: (tableId: string, code: string, kind?: 'reservation' | 'general') => Promise<void>
  assignTableToReservation: (reservationId: string, tableId: string) => Promise<Table | null>
  openReservation: (reservation: Reservation, unlockCode?: string) => Promise<Table | null>
  saveOrderDraft: (tableId: string, items: OrderItem[], immediate?: boolean) => void
  sendOrderToKitchen: (tableId: string, items: OrderItem[]) => void
  updateOrderItems: (orderId: string, items: OrderItem[], immediate?: boolean) => void
  requestPayment: (orderId: string) => void
  getOrderForTableId: (tableId: string) => Order | null
  getUnreadAlertsCount: () => number
  markAlertsRead: () => void
}

const MeseroContext = createContext<MeseroContextValue | null>(null)

const OFFLINE_QUEUE_KEY = 'mfoodx_mesero_offline_queue_v1'
const ALERTS_READ_KEY_PREFIX = 'mfoodx_mesero_alerts_read_at_v1:'

/**
 * Provider del modo “Mesero”.
 *
 * Mantiene estado y reglas de negocio necesarias para la app de mesero:
 * - Polling (core/aux) para tener mesas/órdenes actualizadas.
 * - Cola offline para mutaciones (en localStorage), con flush automático.
 * - Notificaciones/sonidos para eventos de cocina (items ready).
 *
 * Nota: el backend sigue siendo fuente de verdad para totales/precios y autorización.
 */
export function MeseroProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth()
  const apiEnabled = isApiEnabled()
  const currentUser = ((auth.user as User | null) ?? (apiEnabled ? null : (demoUser as User)))
  const currentUserSafe = useMemo<User>(
    () => currentUser ?? ({ id: '', name: '', role: 'waiter' } as User),
    [currentUser],
  )

  const [apiOnline, setApiOnline] = useState(true)
  const [tablesData, setTablesData] = useState<Table[]>(() => (apiEnabled ? [] : demoTables))
  const [ordersData, setOrdersData] = useState<Order[]>(() => (apiEnabled ? [] : demoOrders))
  const [productsData, setProductsData] = useState<Product[]>(() => (apiEnabled ? [] : demoProducts))
  const [categoriesData, setCategoriesData] = useState<Category[]>(() => (apiEnabled ? [] : demoCategories))
  const [activityLogsData, setActivityLogsData] = useState<ActivityLog[]>(() => (apiEnabled ? [] : demoActivityLogs))
  const [zonesData, setZonesData] = useState<Zone[]>([])
  const [reservationsData, setReservationsData] = useState<Reservation[]>([])
  const [offlineQueue, setOfflineQueue] = useLocalStorageJsonState<QueuedMutation[]>(OFFLINE_QUEUE_KEY, [])

  const refreshInFlightRef = useRef(false)
  const refreshCoreInFlightRef = useRef(false)
  const refreshAuxInFlightRef = useRef(false)
  const flushInFlightRef = useRef(false)
  const lastApiErrorToastAtRef = useRef(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const seenReadyAlertIdsRef = useRef<Set<string>>(new Set())
  const readyBellPrimedRef = useRef(false)
  const reservationUnlockBypassRef = useRef<Map<string, number>>(new Map())
  // Set de order IDs con una escritura local en vuelo (PATCH pendiente).
  // Mientras un ID esté aquí, el polling no puede sobreescribir la versión local.
  const pendingOrderWritesRef = useRef<Map<string, number>>(new Map())
  // Contador secuencial de escrituras por orden para ignorar respuestas fuera de orden (stale API responses)
  const orderWriteSeqRef = useRef<Map<string, number>>(new Map())
  // Temporizadores para debouncing de ráfagas rápidas de clics en borradores de comandas
  const debouncedDraftTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const draftOrderIdsRef = useRef<Map<string, string>>(new Map())

  const createId = useCallback((prefix: string) => {
    const random =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    return `${prefix}-${random}`
  }, [])

  const ensureAudioContext = useCallback(async () => {
    if (typeof window === 'undefined') return null
    const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
    const Ctx = w.AudioContext ?? w.webkitAudioContext
    if (!Ctx) return null

    if (!audioContextRef.current) audioContextRef.current = new Ctx()
    const ctx = audioContextRef.current
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume()
      } catch {
        return null
      }
    }
    return ctx
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => {
      void ensureAudioContext()
    }
    window.addEventListener('pointerdown', handler, { once: true })
    return () => window.removeEventListener('pointerdown', handler)
  }, [ensureAudioContext])

  const playReadyBell = useCallback(async () => {
    const ctx = await ensureAudioContext()
    if (!ctx) return

    const t0 = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, t0)
    gain.gain.setValueAtTime(0.0001, t0)
    gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(t0)
    osc.stop(t0 + 0.2)
  }, [ensureAudioContext])

  const sanitizeQueuedMutation = useCallback((mutation: QueuedMutation): QueuedMutation => {
    const payload = mutation.payload
    if (!payload || typeof payload !== 'object') return mutation
    const payloadObj = payload as Record<string, unknown>
    const itemsValue = payloadObj.items
    if (!Array.isArray(itemsValue)) return mutation

    let changed = false
    const map = new Map<string, string>()
    const nextItems = itemsValue.map((raw) => {
      if (!raw || typeof raw !== 'object') return raw
      const rawObj = raw as Record<string, unknown>
      const id = rawObj.id
      if (typeof id === 'string' && id.startsWith('temp-')) {
        const nextId = map.get(id) ?? createId('item')
        map.set(id, nextId)
        changed = true
        return { ...rawObj, id: nextId }
      }
      return raw
    })

    if (!changed) return mutation
    return { ...mutation, payload: { ...payloadObj, items: nextItems } }
  }, [createId])

  useEffect(() => {
    setOfflineQueue((prev) => prev.map(sanitizeQueuedMutation))
  }, [sanitizeQueuedMutation, setOfflineQueue])

  const computeTotals = useCallback((items: OrderItem[]) => computeOrderTotals(items), [])

  const appendActivity = useCallback((entry: Omit<ActivityLog, 'id' | 'timestamp'>) => {
    const log: ActivityLog = {
      id: createId('act'),
      timestamp: new Date(),
      ...entry,
    }
    setActivityLogsData((prev) => [log, ...prev])
  }, [createId])

  const getApiErrorInfo = useCallback((error: unknown) => {
    const errObj = error && typeof error === 'object' ? (error as Record<string, unknown>) : null
    const resp = errObj && errObj.response && typeof errObj.response === 'object' ? (errObj.response as Record<string, unknown>) : null
    const status = resp && typeof resp.status === 'number' ? resp.status : null
    const data = resp && resp.data && typeof resp.data === 'object' ? (resp.data as Record<string, unknown>) : null
    const message =
      (data && typeof data.message === 'string' && data.message) ||
      (error instanceof Error ? error.message : 'Error desconocido')
    return { status, message }
  }, [])

  const reportApiError = useCallback((error: unknown, options?: { force?: boolean }) => {
    const { status, message } = getApiErrorInfo(error)
    setApiOnline(status !== null)
    const now = Date.now()
    const isUserActionError = status === 409 || status === 422 || options?.force === true

    if (isUserActionError || now - lastApiErrorToastAtRef.current > 30_000) {
      if (!isUserActionError) {
        lastApiErrorToastAtRef.current = now
      }
      if (status === null) {
        toast.error('API sin conexión', { description: message })
      } else if (status === 401) {
        toast.error('Sesión expirada', { description: 'Vuelve a iniciar sesión' })
      } else if (status === 409 || status === 422) {
        toast.error(message)
      } else {
        toast.error('Error de API', { description: `${status} ${message}` })
      }
    }
  }, [getApiErrorInfo])

  const enqueueMutation = useCallback((mutation: QueuedMutation) => {
    setOfflineQueue((prev) => mergeOfflineQueueMutation(prev, mutation))

    toast.warning('Acción guardada sin conexión', {
      description: `Pendiente de sincronización (${mutation.kind})`,
    })
  }, [setOfflineQueue])

  const runMutation = useCallback(
    async (mutation: QueuedMutation, promise: Promise<unknown>) => {
      if (!apiEnabled) return
      try {
        await promise
        setApiOnline(true)
      } catch (error) {
        const { status } = getApiErrorInfo(error)
        reportApiError(error)
        if (status === null) enqueueMutation(mutation)
      }
    },
    [apiEnabled, enqueueMutation, getApiErrorInfo, reportApiError],
  )

  const executeQueuedMutation = useCallback(
    async (mutation: QueuedMutation) => {
      if (!apiEnabled) return
      switch (mutation.kind) {
        case 'updateTable':
          await posApi.updateTable(mutation.tableId, mutation.payload)
          return
        case 'updateOrder':
          await posApi.updateOrder(mutation.orderId, mutation.payload)
          return
        case 'createOrder':
          await posApi.createOrder(mutation.payload)
          return
        default:
          return
      }
    },
    [apiEnabled],
  )

  const refreshNow = useCallback(async () => {
    if (!apiEnabled) {
      setApiOnline(true)
      return
    }
    if (refreshInFlightRef.current) return
    refreshInFlightRef.current = true
    try {
      const results = await Promise.allSettled([
        posApi.listTables(),
        posApi.listOrders({ active: true, limit: 200 }),
        posApi.listProducts(),
        posApi.listCategories(),
        posApi.listActivities(),
        posApi.listZones(),
        posApi.listReservations({ scope: 'upcoming' }),
      ])

      const [tablesRes, ordersRes, productsRes, categoriesRes, activitiesRes, zonesRes, reservationsRes] = results

      if (tablesRes.status === 'fulfilled') setTablesData(tablesRes.value)
      if (ordersRes.status === 'fulfilled') setOrdersData(ordersRes.value)
      if (productsRes.status === 'fulfilled') setProductsData(productsRes.value)
      if (categoriesRes.status === 'fulfilled') setCategoriesData(categoriesRes.value)
      if (activitiesRes.status === 'fulfilled') setActivityLogsData(activitiesRes.value)
      if (zonesRes.status === 'fulfilled') setZonesData(zonesRes.value)
      if (reservationsRes.status === 'fulfilled') setReservationsData(reservationsRes.value)

      const anyFulfilled = results.some((r) => r.status === 'fulfilled')
      if (anyFulfilled) setApiOnline(true)
      else reportApiError(results[0].status === 'rejected' ? results[0].reason : undefined)
    } finally {
      refreshInFlightRef.current = false
    }
  }, [apiEnabled, reportApiError])

  const refreshCore = useCallback(async () => {
    if (!apiEnabled) {
      setApiOnline(true)
      return
    }
    if (refreshCoreInFlightRef.current) return
    refreshCoreInFlightRef.current = true
    try {
      const since = activityLogsData[0]?.timestamp?.toISOString?.() ?? ''
      const results = await Promise.allSettled([
        posApi.listTables(),
        posApi.listOrders({ active: true, limit: 200 }),
        posApi.listActivities(since ? { since, limit: 200 } : undefined),
        posApi.listReservations({ scope: 'upcoming' }),
      ])

      const [tablesRes, ordersRes, activitiesRes, reservationsRes] = results
      if (tablesRes.status === 'fulfilled') setTablesData(tablesRes.value)
      if (reservationsRes && reservationsRes.status === 'fulfilled') setReservationsData(reservationsRes.value)

      const availableServerTableIds = new Set<string>()
      if (tablesRes.status === 'fulfilled') {
        for (const t of tablesRes.value) {
          if (t.status === 'available' && !t.currentOrderId) {
            availableServerTableIds.add(t.id)
          }
        }
      }

      if (availableServerTableIds.size > 0) {
        setOfflineQueue((prev) =>
          prev.filter((m) => {
            if (m.kind === 'updateTable' && availableServerTableIds.has(m.tableId)) {
              return false
            }
            if (m.kind === 'updateOrder' || m.kind === 'createOrder') {
              const payload = m.payload as Record<string, unknown> | undefined
              const tableId =
                (m as { tableId?: string }).tableId ??
                (payload?.tableId as string | undefined) ??
                (payload?.table_id as string | undefined)
              if (tableId && availableServerTableIds.has(tableId)) {
                return false
              }
            }
            return true
          })
        )

        for (const tableId of availableServerTableIds) {
          const timer = debouncedDraftTimersRef.current.get(tableId)
          if (timer) {
            clearTimeout(timer)
            debouncedDraftTimersRef.current.delete(tableId)
          }
          const draftOrderId = draftOrderIdsRef.current.get(tableId)
          if (draftOrderId) {
            pendingOrderWritesRef.current.delete(draftOrderId)
            draftOrderIdsRef.current.delete(tableId)
          }
        }
      }

      if (ordersRes.status === 'fulfilled') {
        const incoming = ordersRes.value
        const now = Date.now()
        setOrdersData((prev) => {
          const prevMap = new Map(prev.map((o) => [o.id, o]))
          const merged = incoming.map((serverOrder) => {
            const localOrder = prevMap.get(serverOrder.id)
            if (!localOrder) return serverOrder
            if (serverOrder.tableId && availableServerTableIds.has(serverOrder.tableId)) {
              pendingOrderWritesRef.current.delete(serverOrder.id)
            }
            const pendingExpiry = pendingOrderWritesRef.current.get(serverOrder.id) ?? 0
            if (pendingExpiry > now) return localOrder
            const localTime = toMs(localOrder.updatedAt)
            const serverTime = toMs(serverOrder.updatedAt)
            return localTime >= serverTime ? localOrder : serverOrder
          })
          const incomingMap = new Map(incoming.map((o) => [o.id, o]))
          const next = [...merged]
          for (const localOrder of prev) {
            if (!incomingMap.has(localOrder.id)) {
              if (localOrder.tableId && availableServerTableIds.has(localOrder.tableId)) {
                pendingOrderWritesRef.current.delete(localOrder.id)
                continue
              }
              const pendingExpiry = pendingOrderWritesRef.current.get(localOrder.id) ?? 0
              if (pendingExpiry > now) {
                next.push(localOrder)
              }
            }
          }
          return next
        })
      }
      if (activitiesRes.status === 'fulfilled') {
        const incoming = activitiesRes.value
        if (!since) {
          setActivityLogsData(incoming)
        } else if (incoming.length > 0) {
          setActivityLogsData((prev) => {
            const seen = new Set(prev.map((x) => x.id))
            const next = [...incoming.filter((x) => !seen.has(x.id)), ...prev]
            return next.slice(0, 500)
          })
        }
      }

      const anyFulfilled = results.some((r) => r.status === 'fulfilled')
      if (anyFulfilled) setApiOnline(true)
      else reportApiError(results[0].status === 'rejected' ? results[0].reason : undefined)
    } finally {
      refreshCoreInFlightRef.current = false
    }
  }, [activityLogsData, apiEnabled, reportApiError])

  const refreshAux = useCallback(async () => {
    if (!apiEnabled) {
      setApiOnline(true)
      return
    }
    if (refreshAuxInFlightRef.current) return
    refreshAuxInFlightRef.current = true
    try {
      const results = await Promise.allSettled([
        posApi.listProducts(),
        posApi.listCategories(),
        posApi.listZones(),
      ])

      const [productsRes, categoriesRes, zonesRes] = results
      if (productsRes.status === 'fulfilled') setProductsData(productsRes.value)
      if (categoriesRes.status === 'fulfilled') setCategoriesData(categoriesRes.value)
      if (zonesRes.status === 'fulfilled') setZonesData(zonesRes.value)

      const anyFulfilled = results.some((r) => r.status === 'fulfilled')
      if (anyFulfilled) setApiOnline(true)
      else reportApiError(results[0].status === 'rejected' ? results[0].reason : undefined)
    } finally {
      refreshAuxInFlightRef.current = false
    }
  }, [apiEnabled, reportApiError])

  const flushOfflineQueue = useCallback(async () => {
    if (!apiEnabled) return
    if (flushInFlightRef.current) return
    if (!apiOnline) return
    if (offlineQueue.length === 0) return

    flushInFlightRef.current = true
    try {
      let hadSuccess = false
      for (const mutation of offlineQueue) {
        try {
          await executeQueuedMutation(mutation)
          hadSuccess = true
          setOfflineQueue((prev) => prev.filter((m) => m.id !== mutation.id))
          setApiOnline(true)
        } catch (error) {
          const { status } = getApiErrorInfo(error)
          reportApiError(error)
          if (status === null) break
          setOfflineQueue((prev) => prev.filter((m) => m.id !== mutation.id))
        }
      }
      if (hadSuccess) {
        toast.success('Acciones sincronizadas', { description: 'Cambios enviados a la API' })
        void refreshCore()
        void refreshAux()
      }
    } finally {
      flushInFlightRef.current = false
    }
  }, [apiEnabled, apiOnline, executeQueuedMutation, getApiErrorInfo, offlineQueue, refreshAux, refreshCore, reportApiError, setOfflineQueue])

  useEffect(() => {
    if (!apiEnabled) return
    void refreshCore()
    void refreshAux()
  }, [apiEnabled, refreshAux])

  useEffect(() => {
    if (!apiEnabled) return
    if (!apiOnline) return
    const timer = setInterval(() => {
      void refreshCore()
    }, 3_000)
    return () => clearInterval(timer)
  }, [apiEnabled, apiOnline])

  useEffect(() => {
    if (!apiEnabled) return
    if (!apiOnline) return
    const timer = setInterval(() => {
      void refreshAux()
    }, 30_000)
    return () => clearInterval(timer)
  }, [apiEnabled, apiOnline, refreshAux])

  useEffect(() => {
    if (!apiEnabled) return
    if (apiOnline) return
    void refreshCore()
    const timer = setInterval(() => {
      void refreshCore()
    }, 10_000)
    return () => clearInterval(timer)
  }, [apiEnabled, apiOnline])

  useEffect(() => {
    if (!apiEnabled) return
    void flushOfflineQueue()
  }, [apiEnabled, flushOfflineQueue])

  useEffect(() => {
    if (!apiEnabled) return
    if (!apiOnline) return
    if (offlineQueue.length === 0) return
    const timer = setInterval(() => {
      void flushOfflineQueue()
    }, 5_000)
    return () => clearInterval(timer)
  }, [apiEnabled, apiOnline, flushOfflineQueue, offlineQueue.length])

  const zones = useMemo(() => {
    const fromApi = zonesData
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((z) => z.name)
      .filter(Boolean)
    if (fromApi.length > 0) return fromApi
    const fromTables = Array.from(new Set(tablesData.map((t) => t.zone).filter(Boolean)))
    return fromTables.length > 0 ? fromTables : ['General']
  }, [tablesData, zonesData])

  const getOrderForTableId = useCallback(
    (tableId: string) => {
      const table = tablesData.find((t) => t.id === tableId)
      if (!table) return null

      // Si la mesa está libre y no tiene orden activa asignada, NO debe cargar comandas anteriores o eliminadas.
      if (table.status === 'available' && !table.currentOrderId) {
        return null
      }

      if (table.currentOrderId) {
        const found = ordersData.find((o) => o.id === table.currentOrderId)
        if (found && found.status !== 'closed' && found.status !== 'paid') return found
      }

      return (
        ordersData.find(
          (o) =>
            o.tableId === tableId &&
            o.status !== 'closed' &&
            o.status !== 'paid'
        ) ?? null
      )
    },
    [ordersData, tablesData],
  )

  const openTable = useCallback(
    async (tableId: string, diners?: number): Promise<boolean> => {
      const table = tablesData.find((t) => t.id === tableId)
      if (!table || table.status !== 'available') {
        toast.error('La mesa ya no está disponible')
        void refreshNow()
        return false
      }

      const bypassUntil = reservationUnlockBypassRef.current.get(tableId) ?? 0
      const canBypass = bypassUntil > Date.now()
      let reservationOrder: Order | null = null
      let matchedReservationId: string | null = null

      try {
        const reservations = await posApi.listReservations({ scope: 'upcoming' })
        const now = new Date()
        const currentRes = reservations.find(
          (r) => r.tableId === tableId && r.status === 'scheduled'
        )
        if (currentRes) {
          const timeDiffMs = toMs(currentRes.reservedAt) - now.getTime()
          let shouldLink = false
          if (canBypass) {
            shouldLink = true
          } else if (timeDiffMs <= 0 && timeDiffMs >= -45 * 60 * 1000) {
            shouldLink = true
          }
          if (shouldLink) {
            matchedReservationId = currentRes.id
            reservationOrder = ordersData.find(
              (o) =>
                o.reservationId === currentRes.id &&
                o.status !== 'closed' &&
                o.status !== 'paid'
            ) ?? null
          }
        }
      } catch (e) {
        console.error(e)
      }

      const now = new Date()
      const orderId = reservationOrder ? reservationOrder.id : createId('ord')
      const items = reservationOrder ? reservationOrder.items : []
      const initialStatus = reservationOrder ? reservationOrder.status : 'open'
      const activeReservationId = matchedReservationId ?? reservationOrder?.reservationId

      if (activeReservationId) {
        void posApi
          .updateReservation(activeReservationId, { status: 'seated' })
          .catch(console.error)
      }

      const hasActiveOtherOrder = ordersData.some(
        (o) =>
          o.tableId === tableId &&
          o.status !== 'closed' &&
          o.status !== 'paid' &&
          o.id !== orderId &&
          (!o.reservationId || o.id === table.currentOrderId || (activeReservationId && o.reservationId === activeReservationId))
      )

      if (!hasActiveOtherOrder) {
        const { subtotal, tax, total } = computeTotals(items)
        const newOrder: Order = {
          id: orderId,
          tableId,
          tableNumber: table.number,
          waiter: currentUserSafe.name,
          diners: diners ?? reservationOrder?.diners,
          items,
          status: initialStatus,
          createdAt: reservationOrder ? reservationOrder.createdAt : now,
          updatedAt: now,
          subtotal,
          tax,
          total,
          reservationId: activeReservationId ?? undefined,
        }

        const payload = {
          id: newOrder.id,
          tableId: newOrder.tableId,
          tableNumber: newOrder.tableNumber,
          reservationId: newOrder.reservationId,
          reservation_id: newOrder.reservationId,
          waiter: newOrder.waiter,
          diners: newOrder.diners,
          items: newOrder.items,
          status: newOrder.status,
          subtotal: newOrder.subtotal,
          tax: newOrder.tax,
          total: newOrder.total,
          is_pre_order: false,
        }

        try {
          if (apiEnabled) {
            const savedOrder = await posApi.createOrder(payload)
            setOrdersData((prev) => {
              const exists = prev.find((o) => o.id === savedOrder.id)
              if (exists) {
                return prev.map((o) => (o.id === savedOrder.id ? savedOrder : o))
              }
              return [savedOrder, ...prev]
            })
          } else {
            setOrdersData((prev) => {
              const exists = prev.find((o) => o.id === orderId)
              if (exists) {
                return prev.map((o) => (o.id === orderId ? newOrder : o))
              }
              return [newOrder, ...prev]
            })
          }
        } catch (error) {
          reportApiError(error)
          void refreshNow()
          return false
        }
      }

      setTablesData((prev) => {
        return prev.map((t) => {
          if (t.id !== tableId) return t
          return {
            ...t,
            status: 'occupied' as const,
            currentOrderId: orderId,
            waiter: currentUserSafe.name,
            openedAt: now,
          }
        })
      })

      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: 'table',
        entityId: tableId,
        fromStatus: 'available',
        toStatus: 'occupied',
        action: 'table_opened',
        tableNumber: table.number,
        details: diners ? `Mesa ${table.number} abierta (${diners} comensales)` : `Mesa ${table.number} abierta`,
      })

      return true
    },
    [apiEnabled, appendActivity, computeTotals, createId, currentUserSafe.name, currentUserSafe.role, ordersData, refreshNow, reportApiError, tablesData],
  )

  const unlockTableByCode = useCallback(
    async (tableId: string, code: string, kind?: 'reservation' | 'general') => {
      if (!apiEnabled) return
      const trimmed = code.trim()
      if (trimmed.length < 4) {
        toast.error('Código inválido')
        return
      }

      try {
        let isReservation = false
        if (kind === 'general') {
          await posApi.unlockTable(tableId, trimmed)
        } else if (kind === 'reservation') {
          await posApi.reservationUnlockTable(tableId, trimmed)
          isReservation = true
        } else {
          try {
            await posApi.reservationUnlockTable(tableId, trimmed)
            isReservation = true
          } catch {
            await posApi.unlockTable(tableId, trimmed)
          }
        }

        if (isReservation) {
          reservationUnlockBypassRef.current.set(tableId, Date.now() + 60_000)
        }

        setTablesData((prev) =>
          prev.map((t) =>
            t.id === tableId
              ? { ...t, status: 'available' as const, blockedReason: undefined, currentOrderId: undefined, waiter: undefined, openedAt: undefined }
              : t
          )
        )
        toast.success('Mesa desbloqueada')
        void refreshNow()
      } catch (error) {
        reportApiError(error)
      }
    },
    [apiEnabled, refreshNow, reportApiError]
  )

  const assignTableToReservation = useCallback(
    async (reservationId: string, tableId: string): Promise<Table | null> => {
      if (!apiEnabled) {
        toast.error('Disponible sólo con API')
        return null
      }
      const table = tablesData.find((t) => t.id === tableId)
      try {
        await posApi.updateReservation(reservationId, { tableId, status: 'seated' })
        reservationUnlockBypassRef.current.set(tableId, Date.now() + 5 * 60 * 1000)
        toast.success(`Reserva asignada a Mesa ${table?.number ?? ''} y abierta en salón`)
        await refreshNow()
        return tablesData.find((t) => t.id === tableId) ?? table ?? ({ id: tableId } as Table)
      } catch (err) {
        reportApiError(err, { force: true })
        return null
      }
    },
    [apiEnabled, refreshNow, reportApiError, tablesData],
  )

  const openReservation = useCallback(
    async (reservation: Reservation, unlockCode?: string): Promise<Table | null> => {
      if (!reservation.tableId) {
        toast.error('La reserva no tiene mesa asignada')
        return null
      }
      const table = tablesData.find((t) => t.id === reservation.tableId)
      try {
        const trimmedCode = (unlockCode ?? '').trim()
        if (trimmedCode) {
          await posApi.reservationUnlockTable(reservation.tableId, trimmedCode)
        }
        if (reservation.status === 'scheduled') {
          await posApi.updateReservation(reservation.id, { status: 'seated' })
        }
        reservationUnlockBypassRef.current.set(reservation.tableId, Date.now() + 5 * 60 * 1000)
        toast.success(`Reserva de ${reservation.reserverName} abierta en salón`)
        await refreshNow()
        return tablesData.find((t) => t.id === reservation.tableId) ?? table ?? ({ id: reservation.tableId } as Table)
      } catch (err) {
        reportApiError(err, { force: true })
        return null
      }
    },
    [apiEnabled, refreshNow, reportApiError, tablesData],
  )

  const upsertOrderForTable = useCallback(
    (table: Table, items: OrderItem[], status: Order['status'], send_to_kitchen?: boolean) => {
      const now = new Date()
      const { subtotal, tax, total } = computeTotals(items)
      const existingOrder = table.currentOrderId
        ? (ordersData.find((o) => o.id === table.currentOrderId) ?? null)
        : (table.status !== 'available' ? (ordersData.find((o) => o.tableId === table.id && !o.reservationId && o.status !== 'closed' && o.status !== 'paid') ?? null) : null)
      const orderId = existingOrder ? existingOrder.id : createId('ord')
      const reservationId = existingOrder?.reservationId ?? null
      const isPreOrder = Boolean(existingOrder?.reservationId) && table.status !== 'occupied'

      const existing = existingOrder

      if (existing) {
        const nextSeq = (orderWriteSeqRef.current.get(existing.id) ?? 0) + 1
        orderWriteSeqRef.current.set(existing.id, nextSeq)

        // Marcar la orden como "escritura pendiente" para que el polling no la sobreescriba
        const WRITE_LOCK_MS = 30_000 // 30 segundos de protección
        pendingOrderWritesRef.current.set(existing.id, Date.now() + WRITE_LOCK_MS)
        setOrdersData((prev) =>
          prev.map((o) => (o.id === existing.id ? { ...o, items, status, updatedAt: now, subtotal, tax, total } : o))
        )
        const payload = {
          items,
          status,
          subtotal,
          tax,
          total,
          send_to_kitchen,
          is_pre_order: isPreOrder,
        }
        const POST_WRITE_LOCK_MS = 8_000
        const promise = posApi
          .updateOrder(existing.id, payload)
          .then((savedOrder) => {
            pendingOrderWritesRef.current.set(savedOrder.id, Date.now() + POST_WRITE_LOCK_MS)
            const currentSeq = orderWriteSeqRef.current.get(savedOrder.id)
            if (currentSeq && nextSeq < currentSeq) {
              // Una escritura más reciente fue enviada posteriormente. Ignorar respuesta obsoleta.
              return savedOrder
            }
            const updated = { ...savedOrder, updatedAt: new Date() }
            setOrdersData((prev) => prev.map((o) => (o.id === savedOrder.id ? updated : o)))
            return updated
          })
          .catch((err) => {
            const currentSeq = orderWriteSeqRef.current.get(existing.id)
            if (!currentSeq || nextSeq >= currentSeq) {
              pendingOrderWritesRef.current.delete(existing.id)
            }
            throw err
          })
        void runMutation(
          { id: createId('q'), kind: 'updateOrder', orderId: existing.id, payload, createdAt: now.toISOString() },
          promise,
        )
      } else {
        const nextSeq = (orderWriteSeqRef.current.get(orderId) ?? 0) + 1
        orderWriteSeqRef.current.set(orderId, nextSeq)

        const WRITE_LOCK_MS = 30_000
        const POST_WRITE_LOCK_MS = 8_000
        pendingOrderWritesRef.current.set(orderId, Date.now() + WRITE_LOCK_MS)
        const newOrder: Order = {
          id: orderId,
          tableId: table.id,
          tableNumber: table.number,
          waiter: currentUserSafe.name,
          items,
          status,
          createdAt: now,
          updatedAt: now,
          subtotal,
          tax,
          total,
          reservationId: reservationId ?? undefined,
        }

        setOrdersData((prev) => [newOrder, ...prev])

        const orderPayload = {
          id: orderId,
          tableId: table.id,
          tableNumber: table.number,
          reservationId: reservationId ?? undefined,
          reservation_id: reservationId ?? undefined,
          waiter: currentUserSafe.name,
          items,
          status,
          subtotal,
          tax,
          total,
          send_to_kitchen,
          is_pre_order: isPreOrder,
        }

        const promise = posApi
          .createOrder(orderPayload)
          .then((savedOrder) => {
            pendingOrderWritesRef.current.set(savedOrder.id, Date.now() + POST_WRITE_LOCK_MS)
            const currentSeq = orderWriteSeqRef.current.get(savedOrder.id)
            if (currentSeq && nextSeq < currentSeq) {
              return savedOrder
            }
            const updated = { ...savedOrder, updatedAt: new Date() }
            setOrdersData((prev) => prev.map((o) => (o.id === savedOrder.id ? updated : o)))
            return updated
          })
          .catch((err) => {
            const currentSeq = orderWriteSeqRef.current.get(orderId)
            if (!currentSeq || nextSeq >= currentSeq) {
              pendingOrderWritesRef.current.delete(orderId)
            }
            throw err
          })
        void runMutation(
          { id: createId('q'), kind: 'createOrder', payload: orderPayload, createdAt: now.toISOString() },
          promise,
        )
      }

      if (!isPreOrder) {
        setTablesData((prev) => {
          const next = prev.map((t) => {
            if (t.id !== table.id) return t
            const openedAt = t.openedAt ?? now
            return {
              ...t,
              status: 'occupied' as const,
              currentOrderId: t.currentOrderId ?? orderId,
              waiter: currentUserSafe.name,
              openedAt,
            }
          })
          const tableNext = next.find((t) => t.id === table.id)
          if (tableNext) {
            const tablePayload = {
              status: tableNext.status,
              currentOrderId: tableNext.currentOrderId,
              waiter: tableNext.waiter,
              openedAt: tableNext.openedAt?.toISOString(),
            }
            void runMutation(
              { id: createId('q'), kind: 'updateTable', tableId: table.id, payload: tablePayload, createdAt: now.toISOString() },
              posApi.updateTable(table.id, tablePayload),
            )
          }
          return next
        })
      }
    },
    [computeTotals, createId, currentUserSafe.name, ordersData, runMutation],
  )

  const saveOrderDraft = useCallback(
    (tableId: string, items: OrderItem[], immediate = false) => {
      const table = tablesData.find((t) => t.id === tableId)
      if (!table || (table.status === 'available' && !table.currentOrderId)) return
      const existingOrder = table.currentOrderId
        ? (ordersData.find((o) => o.id === table.currentOrderId) ?? null)
        : (table.status !== 'available' ? (ordersData.find((o) => o.tableId === tableId && !o.reservationId && o.status !== 'closed' && o.status !== 'paid') ?? null) : null)
      const nextStatus = existingOrder && existingOrder.status !== 'open' ? existingOrder.status : 'open'

      // Cancelar cualquier temporizador de borrador pendiente para esta mesa
      const existingTimer = debouncedDraftTimersRef.current.get(tableId)
      if (existingTimer) {
        clearTimeout(existingTimer)
        debouncedDraftTimersRef.current.delete(tableId)
      }

      // Actualización optimista inmediata en estado local para UI instantánea (0ms lag)
      const orderId = existingOrder ? existingOrder.id : (draftOrderIdsRef.current.get(tableId) ?? createId('ord'))
      if (!existingOrder) {
        draftOrderIdsRef.current.set(tableId, orderId)
      }

      const { subtotal, tax, total } = computeTotals(items)
      const now = new Date()

      setOrdersData((prev) => {
        const exists = prev.find((o) => o.id === orderId)
        if (exists) {
          return prev.map((o) => (o.id === orderId ? { ...o, items, updatedAt: now, subtotal, tax, total } : o))
        }
        const newDraftOrder: Order = {
          id: orderId,
          tableId: table.id,
          tableNumber: table.number,
          waiter: currentUserSafe.name,
          items,
          status: nextStatus,
          createdAt: now,
          updatedAt: now,
          subtotal,
          tax,
          total,
        }
        return [newDraftOrder, ...prev]
      })

      const WRITE_LOCK_MS = 30_000
      pendingOrderWritesRef.current.set(orderId, Date.now() + WRITE_LOCK_MS)

      if (immediate) {
        upsertOrderForTable(table, items, nextStatus)
      } else {
        // Debounce de 400ms para ráfagas rápidas de clics (+ / -)
        const timer = setTimeout(() => {
          debouncedDraftTimersRef.current.delete(tableId)
          upsertOrderForTable(table, items, nextStatus)
        }, 400)
        debouncedDraftTimersRef.current.set(tableId, timer)
      }
    },
    [computeTotals, createId, currentUserSafe.name, ordersData, tablesData, upsertOrderForTable],
  )

  const sendOrderToKitchen = useCallback(
    (tableId: string, items: OrderItem[]) => {
      const existingTimer = debouncedDraftTimersRef.current.get(tableId)
      if (existingTimer) {
        clearTimeout(existingTimer)
        debouncedDraftTimersRef.current.delete(tableId)
      }
      const table = tablesData.find((t) => t.id === tableId)
      if (!table || (table.status === 'available' && !table.currentOrderId)) {
        toast.error('La mesa ya no está disponible')
        return
      }
      const existingOrder = table.currentOrderId
        ? (ordersData.find((o) => o.id === table.currentOrderId) ?? null)
        : (table.status !== 'available' ? (ordersData.find((o) => o.tableId === tableId && !o.reservationId && o.status !== 'closed' && o.status !== 'paid') ?? null) : null)
      upsertOrderForTable(
        table,
        items.map((i) => (i.status === 'pending' ? { ...i, status: 'sent' } : i)),
        'in-kitchen',
        true,
      )
      if (items.length > 0) {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: 'order',
          entityId: existingOrder?.id,
          fromStatus: existingOrder?.status ?? 'open',
          toStatus: 'in-kitchen',
          action: 'order_sent',
          tableNumber: table.number,
          details: `Pedido enviado a cocina (${items.length} items)`,
        })
      }
      void refreshAux()
    },
    [appendActivity, currentUserSafe.name, currentUserSafe.role, ordersData, refreshAux, tablesData, upsertOrderForTable],
  )

  const updateOrderItems = useCallback(
    (orderId: string, items: OrderItem[], immediate = false) => {
      const order = ordersData.find((o) => o.id === orderId)
      if (order) {
        const prevById = new Map(order.items.map((i) => [i.id, i] as const))
        for (const item of items) {
          const prev = prevById.get(item.id)
          if (!prev) continue
          if (prev.status !== 'delivered' && item.status === 'delivered') {
            appendActivity({
              actor: currentUserSafe.name,
              actorRole: currentUserSafe.role,
              entityType: 'order-item',
              entityId: item.id,
              fromStatus: prev.status,
              toStatus: 'delivered',
              action: 'item_delivered',
              tableNumber: order.tableNumber,
              details: `${item.productName} entregado`,
            })
          }
        }
      }

      const allDelivered = items.length > 0 && items.every((i) => i.status === 'delivered' || i.status === 'cancelled')
      setOrdersData((prev) =>
        prev.map((o) => {
          if (o.id !== orderId) return o
          const { subtotal, tax, total } = computeTotals(items)
          const nextStatus: Order['status'] = allDelivered ? (o.status === 'pending-payment' ? 'pending-payment' : 'delivered') : o.status
          return { ...o, items, status: nextStatus, subtotal, tax, total, updatedAt: new Date() }
        }),
      )

      if (order && allDelivered && order.status !== 'delivered' && order.status !== 'pending-payment') {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: 'order',
          entityId: orderId,
          fromStatus: order.status,
          toStatus: 'delivered',
          action: 'order_delivered',
          tableNumber: order.tableNumber,
          details: `Pedido entregado`,
        })
      }

      const WRITE_LOCK_MS = 30_000
      pendingOrderWritesRef.current.set(orderId, Date.now() + WRITE_LOCK_MS)

      // Cancelar cualquier temporizador pendiente para esta orden
      const timerKey = `order-${orderId}`
      const existingTimer = debouncedDraftTimersRef.current.get(timerKey)
      if (existingTimer) {
        clearTimeout(existingTimer)
        debouncedDraftTimersRef.current.delete(timerKey)
      }

      const runApiPatch = () => {
        const nextSeq = (orderWriteSeqRef.current.get(orderId) ?? 0) + 1
        orderWriteSeqRef.current.set(orderId, nextSeq)

        const now = new Date().toISOString()
        const payload = allDelivered && order?.status !== 'pending-payment' ? { items, status: 'delivered' } : { items }
        const POST_WRITE_LOCK_MS = 8_000
        const promise = posApi
          .updateOrder(orderId, payload)
          .then((savedOrder) => {
            pendingOrderWritesRef.current.set(savedOrder.id, Date.now() + POST_WRITE_LOCK_MS)
            const currentSeq = orderWriteSeqRef.current.get(savedOrder.id)
            if (currentSeq && nextSeq < currentSeq) {
              return savedOrder
            }
            const updated = { ...savedOrder, updatedAt: new Date() }
            setOrdersData((prev) => prev.map((o) => (o.id === savedOrder.id ? updated : o)))
            return updated
          })
          .catch((err) => {
            const currentSeq = orderWriteSeqRef.current.get(orderId)
            if (!currentSeq || nextSeq >= currentSeq) {
              pendingOrderWritesRef.current.delete(orderId)
            }
            throw err
          })
        void runMutation(
          { id: createId('q'), kind: 'updateOrder', orderId, payload, createdAt: now },
          promise,
        )
      }

      if (immediate || allDelivered) {
        runApiPatch()
      } else {
        // Debounce de 400ms para ráfagas rápidas de clics en MesaItemsPage
        const timer = setTimeout(() => {
          debouncedDraftTimersRef.current.delete(timerKey)
          runApiPatch()
        }, 400)
        debouncedDraftTimersRef.current.set(timerKey, timer)
      }
    },
    [appendActivity, computeTotals, createId, currentUserSafe.name, currentUserSafe.role, ordersData, runMutation],
  )

  const requestPayment = useCallback(
    (orderId: string) => {
      const order = ordersData.find((o) => o.id === orderId)
      if (!order) return

      setOrdersData((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: 'pending-payment', updatedAt: new Date() } : o)))
      setTablesData((prev) => prev.map((t) => (t.id === order.tableId ? { ...t, status: 'pending-payment' as const } : t)))

      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: 'order',
        entityId: orderId,
        fromStatus: order.status,
        toStatus: 'pending-payment',
        action: 'payment_requested',
        tableNumber: order.tableNumber,
        details: `Cobro solicitado`,
      })

      const now = new Date().toISOString()
      void runMutation(
        { id: createId('q'), kind: 'updateOrder', orderId, payload: { status: 'pending-payment' }, createdAt: now },
        posApi.updateOrder(orderId, { status: 'pending-payment' }),
      )
      void runMutation(
        { id: createId('q'), kind: 'updateTable', tableId: order.tableId ?? '', payload: { status: 'pending-payment' }, createdAt: now },
        posApi.updateTable(order.tableId ?? '', { status: 'pending-payment' }),
      )
    },
    [appendActivity, createId, currentUserSafe.name, currentUserSafe.role, ordersData, runMutation],
  )

  const getAlertsReadKey = useCallback(() => {
    return `${ALERTS_READ_KEY_PREFIX}${currentUserSafe.id ?? currentUserSafe.name}`
  }, [currentUserSafe.id, currentUserSafe.name])

  const getUnreadAlertsCount = useCallback(() => {
    if (typeof window === 'undefined') return 0
    const key = getAlertsReadKey()
    const readAtRaw = window.localStorage.getItem(key)
    const readAt = readAtRaw ? Date.parse(readAtRaw) : 0
    return activityLogsData.filter((a) => {
      if (a.action !== 'order_ready' && a.action !== 'order_adjusted') return false
      if (!a.tableNumber) return false
      
      let table: Table | undefined
      if (a.entityType === 'order' && a.entityId) {
        const order = ordersData.find((o) => o.id === a.entityId)
        if (order && order.tableId) {
          table = tablesData.find((t) => t.id === order.tableId)
        }
      }
      if (!table) {
        table = tablesData.find((t) => t.number === a.tableNumber && t.waiter === currentUserSafe.name)
        if (!table) {
          table = tablesData.find((t) => t.number === a.tableNumber)
        }
      }
      
      if (!table) return false
      if (table.waiter !== currentUserSafe.name) return false
      return toMs(a.timestamp) > readAt
    }).length
  }, [activityLogsData, currentUserSafe.name, getAlertsReadKey, tablesData, ordersData])

  useEffect(() => {
    const relevant = activityLogsData.filter((a) => {
      if (a.action !== 'order_ready' && a.action !== 'order_adjusted') return false
      if (!a.tableNumber) return false
      
      let table: Table | undefined
      if (a.entityType === 'order' && a.entityId) {
        const order = ordersData.find((o) => o.id === a.entityId)
        if (order && order.tableId) {
          table = tablesData.find((t) => t.id === order.tableId)
        }
      }
      if (!table) {
        table = tablesData.find((t) => t.number === a.tableNumber && t.waiter === currentUserSafe.name)
        if (!table) {
          table = tablesData.find((t) => t.number === a.tableNumber)
        }
      }
      
      if (!table) return false
      if (table.waiter !== currentUserSafe.name) return false
      return true
    })

    const seen = seenReadyAlertIdsRef.current
    if (!readyBellPrimedRef.current) {
      readyBellPrimedRef.current = true
      for (const a of relevant) seen.add(a.id)
      return
    }

    let hasNew = false
    const newlyAdded: typeof relevant = []
    for (const a of relevant) {
      if (!seen.has(a.id)) {
        hasNew = true
        seen.add(a.id)
        newlyAdded.push(a)
      }
    }
    if (hasNew) void playReadyBell()
    for (const a of newlyAdded.slice(0, 3)) {
      let table: Table | undefined
      if (a.entityType === 'order' && a.entityId) {
        const order = ordersData.find((o) => o.id === a.entityId)
        if (order && order.tableId) {
          table = tablesData.find((t) => t.id === order.tableId)
        }
      }
      if (!table) {
        table = tablesData.find((t) => t.number === a.tableNumber && t.waiter === currentUserSafe.name)
        if (!table) {
          table = tablesData.find((t) => t.number === a.tableNumber)
        }
      }
     /*  const tableDesc = table ? `Mesa ${table.number} (${table.zone})` : `Mesa ${a.tableNumber}`
      if (a.action === 'order_adjusted') {
        toast.warning('Ajuste de comanda (Admin)', { description: tableDesc })
      } else if (a.action === 'order_ready') {
        toast.success('Pedido listo', { description: tableDesc })
      } */
    }
  }, [activityLogsData, currentUserSafe.name, playReadyBell, tablesData, ordersData])

  const markAlertsRead = useCallback(() => {
    if (typeof window === 'undefined') return
    const key = getAlertsReadKey()
    window.localStorage.setItem(key, new Date().toISOString())
  }, [getAlertsReadKey])

  const value = useMemo<MeseroContextValue>(
    () => ({
      apiEnabled,
      apiOnline,
      currentUser: currentUserSafe,
      tables: tablesData,
      orders: ordersData,
      products: productsData,
      categories: categoriesData,
      activityLogs: activityLogsData,
      reservations: reservationsData,
      zones,
      offlineQueueCount: offlineQueue.length,
      refreshNow,
      openTable,
      unlockTableByCode,
      assignTableToReservation,
      openReservation,
      saveOrderDraft,
      sendOrderToKitchen,
      updateOrderItems,
      requestPayment,
      getOrderForTableId,
      getUnreadAlertsCount,
      markAlertsRead,
    }),
    [
      activityLogsData,
      apiEnabled,
      apiOnline,
      assignTableToReservation,
      categoriesData,
      currentUserSafe,
      getOrderForTableId,
      getUnreadAlertsCount,
      markAlertsRead,
      offlineQueue.length,
      openReservation,
      openTable,
      unlockTableByCode,
      ordersData,
      productsData,
      refreshNow,
      requestPayment,
      reservationsData,
      saveOrderDraft,
      sendOrderToKitchen,
      tablesData,
      updateOrderItems,
      zones,
    ],
  )

  if (!currentUser && apiEnabled) return null

  return <MeseroContext.Provider value={value}>{children}</MeseroContext.Provider>
}

export function useMesero() {
  const value = useContext(MeseroContext)
  if (!value) throw new Error('useMesero must be used within MeseroProvider')
  return value
}

